import { useState } from "react";
import { Upload, ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import ImportantDates from "@/components/ImportantDates";
import ThemeTag from "@/components/ThemeTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Application window — keep in sync with APPLICATIONS_OPEN/CLOSE in the backend:
const APPLICATIONS_OPEN = new Date("2026-11-02T00:00:00+01:00");
const APPLICATIONS_CLOSE = new Date("2026-11-29T23:59:59+01:00");

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  gender: "",
  dob: "",
  is_final_year: "",
  is_graduate: "",
  osun: "",
  address: "",
  full_participation: "",
  essay: "",
  headshot_consent: "",
};
const EMPTY_FILES = { proof_of_age: null, nin_id: null, student_id: null, grad_cert: null, cv: null };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ageFromDob = (dob) => {
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const t = new Date();
  let age = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) age -= 1;
  return age;
};

const FileField = ({ label, hint, file, onPick, testid }) => (
  <div className="field full">
    <label>{label}</label>
    <label className={`file-drop ${file ? "has-file" : ""}`} data-testid={testid}>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        hidden
        onChange={(e) => onPick(e.target.files[0] || null)}
      />
      <span>{file ? file.name : hint}</span>
      <Upload size={16} />
    </label>
  </div>
);

const YesNo = ({ label, value, onChange, testid }) => (
  <div className="field">
    <label>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Select one</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  </div>
);

export default function ApplyForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState(EMPTY_FILES);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const isOpen = Date.now() >= APPLICATIONS_OPEN.getTime();
  const isPast = Date.now() > APPLICATIONS_CLOSE.getTime();

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (key) => (file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      setNotice({ type: "err", text: `${file.name} is too large. Maximum size is 5MB.` });
      return;
    }
    setNotice(null);
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const essayWords = form.essay.trim() ? form.essay.trim().split(/\s+/).length : 0;

  const continueToStep2 = () => {
    setNotice(null);
    if (form.first_name.trim().length < 1 || form.last_name.trim().length < 1)
      return setNotice({ type: "err", text: "Please enter your first and last name." });
    if (!EMAIL_RE.test(form.email))
      return setNotice({ type: "err", text: "Please provide a valid email address." });
    if (form.phone.trim().length < 7)
      return setNotice({ type: "err", text: "Please provide a valid phone number." });
    if (!form.gender) return setNotice({ type: "err", text: "Please select your gender." });
    const age = ageFromDob(form.dob);
    if (age === null) return setNotice({ type: "err", text: "Please pick your date of birth." });
    if (age < 20 || age > 28)
      return setNotice({ type: "err", text: "Applicants must be 20 to 28 years of age at the time of application." });
    if (!files.proof_of_age)
      return setNotice({ type: "err", text: "Please attach an official proof of age (birth certificate, driver's licence or international passport)." });
    if (!files.nin_id)
      return setNotice({ type: "err", text: "Please attach a valid NIN ID card." });
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/uploads`, { method: "POST", body: fd });
    const data = await res.json();
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : `Upload failed for ${file.name}.`);
    return data.path;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setNotice(null);
    if (form.is_final_year === "") return setNotice({ type: "err", text: "Please tell us if you are a final-year student." });
    if (form.is_final_year === "yes" && !files.student_id)
      return setNotice({ type: "err", text: "Please attach your most recent Student ID card." });
    if (form.is_graduate === "") return setNotice({ type: "err", text: "Please tell us if you are a graduate." });
    if (form.is_graduate === "yes" && !files.grad_cert)
      return setNotice({ type: "err", text: "Please attach your NYSC Certificate, Degree Certificate, or NYSC ID card." });
    if (form.osun === "") return setNotice({ type: "err", text: "Please tell us if you study or reside in Osun State." });
    if (form.osun === "no") return setNotice({ type: "err", text: "KOSS 2027 is open to applicants who live or study in Osun State." });
    if (form.address.trim().length < 5) return setNotice({ type: "err", text: "Please provide your address." });
    if (form.full_participation !== "yes")
      return setNotice({ type: "err", text: "Applicants must be able to participate fully for the entire event and work constructively with others." });
    if (essayWords > 150) return setNotice({ type: "err", text: "Your essay must not be more than 150 words." });
    if (form.essay.trim().length < 30)
      return setNotice({ type: "err", text: "Please write a short essay describing your leadership or problem-solving experience." });
    if (!files.cv) return setNotice({ type: "err", text: "Please attach your CV or resume." });
    if (form.headshot_consent !== "yes")
      return setNotice({ type: "err", text: "Headshot consent is required to apply." });

    setLoading(true);
    try {
      const paths = {};
      for (const key of ["proof_of_age", "nin_id", "student_id", "grad_cert", "cv"]) {
        if (files[key]) paths[key] = await uploadOne(files[key]);
      }
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        gender: form.gender,
        dob: form.dob,
        proof_of_age: paths.proof_of_age,
        nin_id: paths.nin_id,
        is_final_year: form.is_final_year === "yes",
        student_id: paths.student_id || null,
        is_graduate: form.is_graduate === "yes",
        grad_cert: paths.grad_cert || null,
        osun: form.osun === "yes",
        address: form.address.trim(),
        full_participation: form.full_participation === "yes",
        essay: form.essay.trim(),
        cv: paths.cv,
        headshot_consent: form.headshot_consent === "yes",
      };
      const res = await fetch(`${API}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again.");
      setNotice({
        type: "ok",
        text: `Application received, ${form.first_name}. The organising team will review it and contact shortlisted applicants. Reference: ${data.id.slice(0, 8).toUpperCase()}`,
      });
      setForm(EMPTY);
      setFiles(EMPTY_FILES);
      setStep(1);
    } catch (err) {
      setNotice({ type: "err", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="apply" id="apply">
      <div className="wrap">
        <ImportantDates />
      </div>
      <div className="wrap apply-grid">
        <Reveal>
          <div className="kicker">Applications</div>
          <h2 className="title">Step into the arena.</h2>
          <ThemeTag />
          <p className="intro">
            We are looking for people who are curious, willing to contribute and ready
            to think beyond the obvious. You do not need to have everything figured
            out. You do need to be ready to do the work.
          </p>
          <div className="aside">
            <h3>Application requirements</h3>
            <ol className="reqs">
              <li>Applicants must be 20 to 28 years of age at the time of application.</li>
              <li>Eligible applicants should be final-year students or recent graduates who live or study in Osun State.</li>
              <li>Demonstrated curiosity about leadership, strategy, innovation, entrepreneurship, public service, or community impact.</li>
              <li>Evidence of initiative, problem-solving, teamwork, volunteering, project work, or responsibility in a student or community setting.</li>
              <li>Ability to participate fully for the entire event and work constructively with others.</li>
              <li>A short-written response explaining a problem the applicant would like to solve and why.</li>
            </ol>
            <p>
              <strong>Date:</strong> Saturday, 30 January 2027, 10:00 AM–4:00 PM.{" "}
              <strong>Venue:</strong>{" "}
              <a
                className="map-link"
                href="https://www.google.com/maps/search/?api=1&query=Aurora%20Conference%20Centre%2C%20Osogbo"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="apply-venue-map-link"
              >
                Aurora Conference Centre, Osogbo ↗
              </a>
              , Osun State, Nigeria.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <form id="application-form" onSubmit={onSubmit} data-testid="application-form">
            {!isOpen && (
              <div className="notice preview-banner" role="status" data-testid="applications-preview-banner">
                Preview mode — applications open on 2 November 2026. You can explore the
                form now; submissions will be accepted from 2 November.
              </div>
            )}
            {isPast && (
              <div className="notice preview-banner" role="status" data-testid="applications-closed-banner">
                Applications closed on 29 November 2026. Thank you for your interest in KOSS 2027.
              </div>
            )}
            <div className="steps" data-testid="form-steps">
              <span className={`step-pill ${step === 1 ? "active" : ""}`}>Part 1 · Personal details</span>
              <span className={`step-pill ${step === 2 ? "active" : ""}`}>Part 2 · Eligibility & experience</span>
            </div>
            {notice && (
              <div className={`notice ${notice.type}`} role="status" data-testid="application-form-notice">
                {notice.text}
              </div>
            )}
            {step === 1 && (
              <div className="fields">
                <div className="field">
                  <label htmlFor="first_name">First name *</label>
                  <input id="first_name" required autoComplete="given-name" placeholder="First name"
                    value={form.first_name} onChange={set("first_name")} data-testid="application-form-input-first-name" />
                </div>
                <div className="field">
                  <label htmlFor="last_name">Last name *</label>
                  <input id="last_name" required autoComplete="family-name" placeholder="Last name"
                    value={form.last_name} onChange={set("last_name")} data-testid="application-form-input-last-name" />
                </div>
                <div className="field">
                  <label htmlFor="email">Email address *</label>
                  <input id="email" type="email" required autoComplete="email" placeholder="you@example.com"
                    value={form.email} onChange={set("email")} data-testid="application-form-input-email" />
                </div>
                <div className="field">
                  <label htmlFor="phone">Phone number *</label>
                  <input id="phone" type="tel" required autoComplete="tel" placeholder="+234..."
                    value={form.phone} onChange={set("phone")} data-testid="application-form-input-phone" />
                </div>
                <div className="field">
                  <label htmlFor="gender">Gender *</label>
                  <select id="gender" required value={form.gender} onChange={set("gender")} data-testid="application-form-select-gender">
                    <option value="">Select one</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="dob">Age before next birthday (date of birth) *</label>
                  <input id="dob" type="date" required min="1998-01-31" max="2007-01-30"
                    value={form.dob} onChange={set("dob")} data-testid="application-form-input-dob" />
                </div>
                <FileField
                  label="Official proof of age *"
                  hint="Attach birth certificate, driver's licence or international passport (PDF/JPG/PNG, max 5MB)"
                  file={files.proof_of_age}
                  onPick={pick("proof_of_age")}
                  testid="application-form-file-proof-of-age"
                />
                <FileField
                  label="Valid NIN ID card *"
                  hint="Attach your National Identification Number (NIN) ID card (PDF/JPG/PNG, max 5MB)"
                  file={files.nin_id}
                  onPick={pick("nin_id")}
                  testid="application-form-file-nin-id"
                />
                <div className="field full step-actions">
                  <button type="button" className="btn" onClick={continueToStep2} data-testid="application-form-continue-btn">
                    Save & Continue →
                  </button>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="fields">
                <YesNo label="Are you a final-year student? *" value={form.is_final_year}
                  onChange={(v) => setForm((f) => ({ ...f, is_final_year: v }))} testid="application-form-select-final-year" />
                <YesNo label="Are you a graduate? *" value={form.is_graduate}
                  onChange={(v) => setForm((f) => ({ ...f, is_graduate: v }))} testid="application-form-select-graduate" />
                {form.is_final_year === "yes" && (
                  <FileField
                    label="Most recent Student ID card *"
                    hint="Attach your student ID card (PDF/JPG/PNG, max 5MB)"
                    file={files.student_id}
                    onPick={pick("student_id")}
                    testid="application-form-file-student-id"
                  />
                )}
                {form.is_graduate === "yes" && (
                  <FileField
                    label="NYSC / Degree certificate *"
                    hint="Attach your NYSC Certificate, Degree Certificate, or NYSC ID card (PDF/JPG/PNG, max 5MB)"
                    file={files.grad_cert}
                    onPick={pick("grad_cert")}
                    testid="application-form-file-grad-cert"
                  />
                )}
                <YesNo label="Do you study or reside in Osun State? *" value={form.osun}
                  onChange={(v) => setForm((f) => ({ ...f, osun: v }))} testid="application-form-select-osun" />
                <div className="field">
                  <label htmlFor="address">Address *</label>
                  <input id="address" required placeholder="Your residential or school address"
                    value={form.address} onChange={set("address")} data-testid="application-form-input-address" />
                </div>
                <div className="field full">
                  <YesNo label="Will you be able to participate fully for the entire event and work constructively with others? *"
                    value={form.full_participation}
                    onChange={(v) => setForm((f) => ({ ...f, full_participation: v }))} testid="application-form-select-participation" />
                </div>
                <div className="field full">
                  <label htmlFor="essay">
                    Have you demonstrated leadership, solved a problem, worked on a project or with a team, volunteered,
                    contributed to public service or a community project, or implemented a strategy? If yes, describe
                    your experience (max 150 words) *
                  </label>
                  <textarea id="essay" required
                    placeholder="Describe your experience in not more than 150 words…"
                    value={form.essay} onChange={set("essay")} data-testid="application-form-input-essay" />
                  <div className={`word-count ${essayWords > 150 ? "over" : ""}`} data-testid="essay-word-count">
                    {essayWords}/150 words
                  </div>
                </div>
                <FileField
                  label="CV or Resume *"
                  hint="Attach your CV or resume (PDF/JPG/PNG, max 5MB)"
                  file={files.cv}
                  onPick={pick("cv")}
                  testid="application-form-file-cv"
                />
                <div className="field full">
                  <YesNo label="All selected participants will take a professional headshot photograph at the event, which may be used for promotional purposes for the summit. Do you give your consent? *"
                    value={form.headshot_consent}
                    onChange={(v) => setForm((f) => ({ ...f, headshot_consent: v }))} testid="application-form-select-headshot-consent" />
                </div>
                <div className="field full step-actions">
                  <button type="button" className="btn outline dark-outline" onClick={() => setStep(1)} data-testid="application-form-back-btn">
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button className="btn" type="submit" disabled={loading} data-testid="application-form-submit-btn">
                    {loading ? "Submitting…" : "Submit Application →"}
                  </button>
                </div>
              </div>
            )}
            <p className="hint">
              Applications are stored securely and reviewed by the organising team. Only
              shortlisted applicants will be contacted.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
